// FILE: src/components/NavBar.jsx

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Slide,
  Toolbar,
  Paper,
  Menu,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import DividerMui from "@mui/material/Divider"; // used inside the mobile drawer
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";

import brandLogo from "../public/olo.png"; // Adjust path if needed
import API from "../api";
import CurrencySelectorDialog from "./CurrencySelectorDialog";

/** Minimal NavLink style */
const NavLink = styled(Link)(({ theme }) => ({
  color: "#333",
  textDecoration: "none",
  fontWeight: 600,
  marginRight: theme.spacing(2),
  padding: theme.spacing(0.5, 1),
  transition: "color 0.2s ease",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

/** Brand logo styling */
const LogoImage = styled("img")({
  height: 60,
  width: "auto",
  marginRight: 2,
});

/** Profile dropdown — scoped CSS (navpm- prefix so nothing leaks in or out) */
const PROFILE_MENU_CSS = `
.navpm {
  padding: 14px;
  background: #fff;
  color: #111827;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
}
.navpm * { box-sizing: border-box; }
.navpm-label {
  font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: #9CA3AF;
  margin: 2px 4px 8px;
}
.navpm-user {
  display: flex; align-items: center; gap: 12px;
  padding: 2px 4px 12px;
}
.navpm-avatar-wrap { position: relative; flex-shrink: 0; }
.navpm-online-dot {
  position: absolute; right: 0; bottom: 2px;
  width: 11px; height: 11px; border-radius: 50%;
  background: #22C55E; border: 2px solid #fff;
}
.navpm-name { font-size: 15px; font-weight: 700; line-height: 1.2; color: #111827; }
.navpm-role { font-size: 12.5px; color: #6B7280; margin-top: 1px; }
.navpm-online {
  display: flex; align-items: center; gap: 5px;
  font-size: 11.5px; color: #16A34A; margin-top: 3px;
}
.navpm-online-mini { width: 7px; height: 7px; border-radius: 50%; background: #22C55E; }
.navpm-edit {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 12px 14px; margin-bottom: 14px;
  background: #FFFBEB; border: 1px solid #F5D879; border-radius: 12px;
  font: inherit; font-size: 13.5px; font-weight: 600; color: #111827;
  cursor: pointer; transition: background 0.15s ease;
}
.navpm-edit:hover { background: #FFF3C4; }
.navpm-chev { margin-left: auto; color: #9CA3AF; display: flex; }
.navpm-edit .navpm-chev { color: #B45309; }
.navpm-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
  margin-bottom: 14px;
}
.navpm-card {
  display: flex; flex-direction: column; align-items: center; gap: 7px;
  padding: 14px 8px 12px;
  background: #fff; border: 1px solid #E5E7EB; border-radius: 12px;
  font: inherit; font-size: 12.5px; font-weight: 600; color: #374151;
  cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease;
}
.navpm-card:hover { border-color: #FFCE1F; background: #FFFDF4; }
.navpm-card svg { font-size: 22px; color: #4B5563; }
.navpm-rows { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
.navpm-row {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 9px 12px;
  background: #fff; border: 1px solid #E5E7EB; border-radius: 12px;
  font: inherit; font-size: 13px; font-weight: 600; color: #374151;
  cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease;
}
.navpm-row:hover { border-color: #FFCE1F; background: #FFFDF4; }
.navpm-row-ic {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 8px;
  background: #F3F4F6; color: #4B5563; flex-shrink: 0;
}
.navpm-row-ic svg { font-size: 16px; }
.navpm-logout {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 12px 14px;
  background: #FEF2F2; border: 1px solid #FECACA; border-radius: 12px;
  font: inherit; font-size: 13.5px; font-weight: 600; color: #DC2626;
  cursor: pointer; transition: background 0.15s ease;
}
.navpm-logout:hover { background: #FEE2E2; }
.navpm-logout svg { font-size: 18px; }
`;

export default function NavBar() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Token & Role from localStorage
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("userRole");

  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  /**
   * Conditionally define nav items
   * - Hide "Z Reports" if userRole === "Manager" or "Accountant"
   * - Show "Shifts" if userRole is "Admin", "Manager" or "Accountant"
   * - Show "Advance Reports" ONLY if userRole is "Admin" or "Accountant"
   */
  const navItems = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/transactions", label: "Transactions" },
    { to: "/reconciliations", label: "Reconciliation" },
    { to: "/detailed-report", label: "Ledger" },
    ...(userRole !== "Manager" && userRole !== "Accountant"
      ? [{ to: "/zreport-list", label: "Z Reports" }]
      : []),
    ...(["Admin", "Manager", "Accountant"].includes(userRole)
      ? [{ to: "/shifts", label: "Shifts" }]
      : []),
    ...(["Admin", "Accountant"].includes(userRole)
      ? [{ to: "/advance-reports", label: "Advance Reports" }]
      : []),
  ];

  // User profile info
  const [userProfile, setUserProfile] = useState({
    displayName: "",
    signedProfileUrl: "",
    currency: "USD",
  });

  // Currency dialog (only for Admin)
  const [openCurrencyDialog, setOpenCurrencyDialog] = useState(false);

  // Fetch user profile if logged in
  useEffect(() => {
    async function fetchUserProfile() {
      try {
        const res = await API.get("/users/me");
        setUserProfile(res.data);
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    }
    if (token) {
      fetchUserProfile();
    }
  }, [token]);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleUserIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    navigate("/auth");
  };

  // Drawer content for mobile
  const drawerContent = (
    <Box
      sx={{
        width: 240,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
        <LogoImage src={brandLogo} alt="Brand Logo" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {/* Brand Name */}
        </Typography>
      </Box>
      <DividerMui />
      <List sx={{ flexGrow: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(item.to);
                setDrawerOpen(false);
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ sx: { fontWeight: 600 } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <DividerMui />
    </Box>
  );

  return (
    <>
      <Slide direction="down" in mountOnEnter unmountOnExit>
        <Paper
          component="header"
          elevation={3}
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            backgroundColor: "#fff",
            color: "#333",
          }}
        >
          <Toolbar
            sx={{
              minHeight: 64,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {/* Mobile: hamburger + brand */}
            {isMobile && (
              <IconButton
                edge="start"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: "#333", mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Desktop brand area */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LogoImage src={brandLogo} alt="Brand Logo" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {/* Brand Name */}
                </Typography>
              </Box>
            )}

            {/* For mobile also show brand */}
            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LogoImage src={brandLogo} alt="Brand Logo" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {/* Brand Name */}
                </Typography>
              </Box>
            )}

            {/* Desktop Nav Items */}
            {!isMobile && (
              <Box sx={{ ml: 3, flex: 1 }}>
                {navItems.map((item) => (
                  <NavLink key={item.label} to={item.to}>
                    {item.label}
                  </NavLink>
                ))}
              </Box>
            )}

            {/* Right side: user avatar and menu */}
            {token && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="User Menu" arrow>
                  <IconButton onClick={handleUserIconClick} sx={{ color: "#333" }}>
                    {userProfile.signedProfileUrl ? (
                      <Avatar src={userProfile.signedProfileUrl} />
                    ) : (
                      <Avatar>
                        {userProfile.displayName
                          ? userProfile.displayName.charAt(0).toUpperCase()
                          : <PersonIcon fontSize="small" />}
                      </Avatar>
                    )}
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  MenuListProps={{ sx: { p: 0 } }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      width: 340,
                      maxWidth: "calc(100vw - 24px)",
                      borderRadius: "16px",
                      border: "1px solid #EEECE7",
                      boxShadow: "0 12px 40px rgba(15, 23, 42, 0.16)",
                      overflow: "hidden",
                    },
                  }}
                >
                  <style>{PROFILE_MENU_CSS}</style>
                  <div className="navpm">
                    {/* ACCOUNT */}
                    <div className="navpm-label">Account</div>
                    <div className="navpm-user">
                      <div className="navpm-avatar-wrap">
                        <Avatar
                          src={userProfile.signedProfileUrl || undefined}
                          sx={{
                            width: 46,
                            height: 46,
                            fontSize: 18,
                            fontWeight: 700,
                            bgcolor: "#FFF3C4",
                            color: "#92400E",
                          }}
                        >
                          {userProfile.displayName ? (
                            userProfile.displayName.charAt(0).toUpperCase()
                          ) : (
                            <PersonIcon fontSize="small" />
                          )}
                        </Avatar>
                        <span className="navpm-online-dot" />
                      </div>
                      <div>
                        <div className="navpm-name">
                          {userProfile.displayName || "No Name Set"}
                        </div>
                        <div className="navpm-role">
                          {userRole === "Admin" ? "Administrator" : userRole || "User"}
                        </div>
                        <div className="navpm-online">
                          <span className="navpm-online-mini" /> Online
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="navpm-edit"
                      onClick={() => {
                        handleMenuClose();
                        navigate("/profile");
                      }}
                    >
                      <PersonOutlineOutlinedIcon fontSize="small" />
                      Edit Profile
                      <span className="navpm-chev">
                        <ChevronRightRoundedIcon fontSize="small" />
                      </span>
                    </button>

                    {/* MANAGE — same role rules as before */}
                    {["Admin", "Manager", "Accountant"].includes(userRole) && (
                      <>
                        <div className="navpm-label">Manage</div>
                        <div className="navpm-grid">
                          <button
                            type="button"
                            className="navpm-card"
                            onClick={() => {
                              handleMenuClose();
                              navigate("/manage-tilluser");
                            }}
                          >
                            <PeopleAltOutlinedIcon />
                            Manage Tilluser
                          </button>
                          {userRole === "Admin" && (
                            <>
                              <button
                                type="button"
                                className="navpm-card"
                                onClick={() => {
                                  handleMenuClose();
                                  navigate("/cash-accounts");
                                }}
                              >
                                <AccountBalanceOutlinedIcon />
                                Manage Accounts
                              </button>
                              <button
                                type="button"
                                className="navpm-card"
                                onClick={() => {
                                  handleMenuClose();
                                  navigate("/manage-users");
                                }}
                              >
                                <GroupOutlinedIcon />
                                Manage Users
                              </button>
                            </>
                          )}
                          {(userRole === "Admin" || userRole === "Accountant") && (
                            <button
                              type="button"
                              className="navpm-card"
                              onClick={() => {
                                handleMenuClose();
                                navigate("/categories");
                              }}
                            >
                              <LayersOutlinedIcon />
                              Manage Categories
                            </button>
                          )}
                        </div>
                      </>
                    )}

                    {/* CONTROL CENTRE — Admin-only, as before */}
                    {userRole === "Admin" && (
                      <>
                        <div className="navpm-label">Control Centre</div>
                        <div className="navpm-rows">
                          <button
                            type="button"
                            className="navpm-row"
                            onClick={() => {
                              handleMenuClose();
                              setOpenCurrencyDialog(true);
                            }}
                          >
                            <span className="navpm-row-ic">
                              <PaidOutlinedIcon />
                            </span>
                            Change Currency
                            <span className="navpm-chev">
                              <ChevronRightRoundedIcon fontSize="small" />
                            </span>
                          </button>
                          <button
                            type="button"
                            className="navpm-row"
                            onClick={() => {
                              handleMenuClose();
                              navigate("/audit-logs");
                            }}
                          >
                            <span className="navpm-row-ic">
                              <AssignmentOutlinedIcon />
                            </span>
                            Audit Logs
                            <span className="navpm-chev">
                              <ChevronRightRoundedIcon fontSize="small" />
                            </span>
                          </button>
                          <button
                            type="button"
                            className="navpm-row"
                            onClick={() => {
                              handleMenuClose();
                              navigate("/reconciliation-settings");
                            }}
                          >
                            <span className="navpm-row-ic">
                              <SettingsOutlinedIcon />
                            </span>
                            Reconciliation Settings
                            <span className="navpm-chev">
                              <ChevronRightRoundedIcon fontSize="small" />
                            </span>
                          </button>
                        </div>
                      </>
                    )}

                    <button
                      type="button"
                      className="navpm-logout"
                      onClick={() => {
                        handleMenuClose();
                        handleLogout();
                      }}
                    >
                      <LogoutOutlinedIcon />
                      Logout
                    </button>
                  </div>
                </Menu>
              </Box>
            )}
          </Toolbar>

          {/* Drawer for mobile */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
          >
            {drawerContent}
          </Drawer>
        </Paper>
      </Slide>

      {/* Currency Selector Dialog - only for Admin */}
      {userRole === "Admin" && (
        <CurrencySelectorDialog
          open={openCurrencyDialog}
          onClose={() => setOpenCurrencyDialog(false)}
          currentCurrency={userProfile.currency || "USD"}
          onCurrencyChange={(newCurrency) =>
            setUserProfile((prev) => ({ ...prev, currency: newCurrency }))
          }
        />
      )}
    </>
  );
}
